import { type ProtocoloTramitacaoResponse } from '@/core/api/sicom-api/servicos/shared/protocolo-tramitacao.model';
import { MascaraUtil } from '@/shared/utils/mascara-util';
import { type ProtocoloResumo, type TramitacaoHistoricoCard } from '@/features/servicos/shared/card-servicos/card-servicos';
import { type ItemStatus } from '@/features/servicos/shared/models/item-status.model';

export interface ProtocoloConsultaPadrao {
    numeroProtocolo?: string;
    anoProtocolo?: string;
    ufCrm?: string;
}

export function mapearItemStatusProtocolo(response: Record<string, unknown>, index: number, ufCrmPadrao: string, protocoloPadrao?: ProtocoloConsultaPadrao): ItemStatus {
    const numeroProtocolo = obterTextoResponse(response, ['numeroProtocolo', 'numProtocolo', 'nrProtocolo', 'numero_protocolo']) ?? protocoloPadrao?.numeroProtocolo;
    const anoProtocolo = obterTextoResponse(response, ['anoProtocolo', 'ano', 'ano_protocolo']) ?? protocoloPadrao?.anoProtocolo;
    const portalApi = obterTextoResponse(response, ['portal', 'protocoloPortal']);
    const portal = portalApi ?? (numeroProtocolo && anoProtocolo ? `${numeroProtocolo}/${anoProtocolo}` : `ABERTO-${index + 1}`);

    const sas = obterTextoResponse(response, ['numeroSequencialUnico', 'sas', 'protocoloSas', 'protocolo_sas']) ?? '';
    const empresa = obterTextoResponse(response, ['razaoSocialPrestador', 'razaoSocial', 'nomeFantasia', 'empresa', 'prestador', 'nome']) ?? 'Protocolo consultado';
    const avatar = normalizarAvatarMedico(response);

    // 'numeroCrmOrigem' (VistoDTO, visto corrido/fracionado) entra DEPOIS dos aliases existentes:
    // 'crmEmpresa' precisa continuar em primeiro porque, em pessoa jurídica, é o CRM da empresa
    // que identifica o card — não o do solicitante.
    const crm = obterTextoResponse(response, ['crmEmpresa', 'numCrm', 'crm', 'numeroCrmOrigem']);
    // 'ufOrigem' entra por simetria/tolerância ao nome que o usuário usou para descrever o par
    // CRM/UF do visto; nenhum DTO real hoje usa esse nome (o campo real é 'ufCrmOrigem', já coberto).
    const uf = obterTextoResponse(response, ['ufPrestador', 'ufCrmOrigem', 'ufcrm', 'uf', 'sgUf', 'ufOrigem']);
    const ufCrm = (uf ?? protocoloPadrao?.ufCrm ?? ufCrmPadrao)?.trim().toUpperCase();
    const crmUf = crm && uf ? `${crm}-${uf}` : undefined;
    // Nulo é o retorno esperado na maioria dos serviços: só o pré-prestador traz CNPJ, e mesmo lá
    // ele falta com frequência. `undefined` faz o card omitir a linha, em vez de exibir placeholder.
    const cnpj = formatarCnpjResponse(obterTextoResponse(response, ['cnpj', 'nuCnpj', 'numeroCnpj', 'nuCpfCgc']));
    const diretorTecnicoNome = obterTextoResponse(response, ['nomeDiretorTecnico', 'diretorTecnicoNome', 'nomeDiretor', 'nomeMedicoDiretorTecnico', 'nomeMedico', 'nomeDt', 'nome']);
    // 'crmSolicitante' fica por ULTIMO de proposito: e o nome que a LISTAGEM do pre-prestador usa
    // (PrePrestadorSinteticoResponse), onde o medico solicitante e o proprio diretor tecnico. O
    // detalhe manda 'numeroCrmDt', explicito, e precisa continuar ganhando quando os dois vierem.
    //
    // Sem este alias o card de PJ ficava sem rosto: o nome aparecia (via 'nomeMedico', que ja estava
    // na lista irma) mas o CRM nao, e `separarCrmUf` devolve null sem CRM — a biometria nunca era
    // consultada. Nome com alias e CRM sem era a assimetria que escondia o defeito.
    const diretorTecnicoCrm = obterTextoResponse(response, ['numeroCrmDt', 'numCrmDt', 'crmDiretorTecnico', 'crmDiretor', 'crmDt', 'numCrmDiretor', 'crmSolicitante']);
    const diretorTecnicoUf = obterTextoResponse(response, ['ufCrmDt', 'ufDiretorTecnico', 'ufCrmDiretor', 'ufDt', 'ufCrm']);
    const diretorTecnicoCrmUf = diretorTecnicoCrm && diretorTecnicoUf ? `${diretorTecnicoCrm}-${diretorTecnicoUf}` : diretorTecnicoCrm;

    const situacaoBruta = obterTextoResponse(response, ['situacao', 'status', 'situacaoCartao']) ?? '';
    const status = mapearStatusProtocolo(situacaoBruta);
    const situacaoCodigo = extrairSituacaoCodigo(situacaoBruta);
    const inPago = obterBooleanResponse(response, ['inPago', 'isPago']);
    const isAnuidadePaga = obterBooleanResponse(response, ['isAnuidadePaga', 'anuidadePaga']);
    const modalidadeInscricao = obterTextoResponse(response, ['modalidadeInscricao', 'inRegCad', 'IN_REG_CAD', 'in_reg_cad']);
    const indicacaoDiretorTecnico = mapearIndicacaoDiretorTecnico(response);
    const ultimaTramitacao = mapearUltimaTramitacao(response, { numeroProtocolo, anoProtocolo, ufCrm });
    // Lida SEMPRE, e não só quando falta tramitação: `mapearUltimaTramitacao` usa `dataHora` apenas
    // como último recurso, então em protocolo já tramitado a abertura se perdia — justo a data que
    // conta o decurso de prazo da inscrição de ofício.
    const dataSolicitacao = obterTextoResponse(response, ['dataHora', 'data_hora', 'dataSolicitacao', 'data_solicitacao', 'dataCriacao', 'data_criacao']);

    return {
        portal,
        sas,
        empresa,
        avatar,
        crmUf,
        cnpj,
        diretorTecnicoNome,
        diretorTecnicoCrmUf,
        numeroProtocolo,
        anoProtocolo,
        ufCrm,
        situacaoCodigo,
        dataSolicitacao,
        inPago,
        isAnuidadePaga,
        modalidadeInscricao,
        indicacaoDiretorTecnico,
        ultimaTramitacao,
        status
    };
}

export function mapearHistoricoTramitacaoCard(item: ProtocoloTramitacaoResponse, protocoloSelecionado: ProtocoloResumo | null): TramitacaoHistoricoCard {
    return {
        numeroProtocolo: item.numeroProtocolo ?? protocoloSelecionado?.numeroProtocolo,
        anoProtocolo: item.anoProtocolo ?? protocoloSelecionado?.anoProtocolo,
        ufProtocolo: item.ufProtocolo ?? item.uf_protocolo ?? protocoloSelecionado?.ufCrm,
        textoInformativo: item.textoInformativo,
        dataTramitacao: item.dataTramitacao
    };
}

export function mapearStatusProtocolo(valor?: string): ItemStatus['status'] {
    const status = (valor ?? '').toUpperCase();
    if (status.includes('ACEIT')) {
        return 'ACEITOU';
    }
    if (status.includes('RECUS')) {
        return 'RECUSOU';
    }
    if (status.includes('AGUARD') || status.includes('PEND')) {
        return 'AGUARDANDO';
    }
    return 'SEM INDICACAO';
}

function mapearIndicacaoDiretorTecnico(response: Record<string, unknown>): ItemStatus['indicacaoDiretorTecnico'] {
    const indicado = obterTextoResponse(response, ['indicado', 'inIndicacaoDt', 'inIndicacaoDT', 'in_indicacao_dt', 'IN_INDICACAO_DT']);
    const aprovado = obterTextoResponse(response, ['aprovado', 'inAprovadoDt', 'inAprovadoDT', 'in_aprovado_dt', 'IN_APROVADO_DT']);

    switch ((indicado ?? '').trim().toUpperCase()) {
        case 'S':
        case 'SIM':
        case 'TRUE':
        case '1':
            switch ((aprovado ?? '').trim().toUpperCase()) {
                case 'S':
                case 'SIM':
                case 'TRUE':
                case '1':
                    return 'ACEITOU';
                case 'N':
                case 'NAO':
                case 'NÃO':
                case 'FALSE':
                case '0':
                    return 'RECUSOU';
                default:
                    return 'AGUARDANDO';
            }
        case 'N':
        case 'NAO':
        case 'NÃO':
        case 'FALSE':
        case '0':
            return 'NAO_SOCIO';
        default:
            return undefined;
    }
}

function extrairSituacaoCodigo(valor?: string): string | undefined {
    const texto = (valor ?? '').trim().toUpperCase();
    if (!texto) {
        return undefined;
    }

    if (['A', 'T', 'P', 'C', 'E'].includes(texto)) {
        return texto;
    }

    const primeiraLetra = texto.charAt(0);
    if (['A', 'T', 'P', 'C', 'E'].includes(primeiraLetra)) {
        return primeiraLetra;
    }

    return undefined;
}

function mapearUltimaTramitacao(response: Record<string, unknown>, protocolo: ProtocoloConsultaPadrao): ItemStatus['ultimaTramitacao'] {
    const tramitacao = obterObjetoResponse(response, ['ultimaTramitacao', 'ultima_tramitacao']);
    if (tramitacao) {
        return {
            numeroProtocolo: obterTextoResponse(tramitacao, ['numeroProtocolo', 'numProtocolo', 'num_protocolo']) ?? protocolo.numeroProtocolo,
            anoProtocolo: obterTextoResponse(tramitacao, ['anoProtocolo', 'ano_protocolo']) ?? protocolo.anoProtocolo,
            textoInformativo: obterTextoResponse(tramitacao, ['textoInformativo', 'texto_informativo', 'observacao']),
            dataTramitacao: obterTextoResponse(tramitacao, ['dataTramitacao', 'data_tramitacao', 'dataHora', 'data_hora']),
            ufProtocolo: obterTextoResponse(tramitacao, ['ufProtocolo', 'uf_protocolo', 'uf']) ?? protocolo.ufCrm
        };
    }

    const dataCriacao = obterTextoResponse(response, ['dataHora', 'data_hora', 'dataCriacao', 'data_criacao', 'dataSolicitacao', 'data_solicitacao', 'dtCriacao', 'dt_criacao']);
    if (!dataCriacao) {
        return undefined;
    }

    return {
        numeroProtocolo: protocolo.numeroProtocolo,
        anoProtocolo: protocolo.anoProtocolo,
        dataTramitacao: dataCriacao,
        ufProtocolo: protocolo.ufCrm
    };
}

function obterObjetoResponse(response: Record<string, unknown>, chaves: string[]): Record<string, unknown> | undefined {
    const indiceNormalizado = new Map<string, unknown>();

    for (const [chave, valor] of Object.entries(response)) {
        indiceNormalizado.set(normalizarChaveResponse(chave), valor);
    }

    for (const chave of chaves) {
        const valor = response[chave] ?? indiceNormalizado.get(normalizarChaveResponse(chave));
        if (valor && typeof valor === 'object' && !Array.isArray(valor)) {
            return valor as Record<string, unknown>;
        }
    }

    return undefined;
}

export function obterTextoResponse(response: Record<string, unknown>, chaves: string[]): string | undefined {
    const indiceNormalizado = new Map<string, unknown>();

    for (const [chave, valor] of Object.entries(response)) {
        indiceNormalizado.set(normalizarChaveResponse(chave), valor);
    }

    for (const chave of chaves) {
        const valor = response[chave] ?? indiceNormalizado.get(normalizarChaveResponse(chave));
        if (valor === null || valor === undefined) {
            continue;
        }

        const texto = String(valor).trim();
        if (texto) {
            return texto;
        }
    }

    return undefined;
}

function normalizarChaveResponse(chave: string): string {
    return chave.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
}

function obterBooleanResponse(response: Record<string, unknown>, chaves: string[]): boolean | undefined {
    for (const chave of chaves) {
        const valor = response[chave];
        if (valor === null || valor === undefined) {
            continue;
        }

        if (typeof valor === 'boolean') {
            return valor;
        }

        if (typeof valor === 'number') {
            if (valor === 1) {
                return true;
            }

            if (valor === 0) {
                return false;
            }

            continue;
        }

        const texto = String(valor).trim().toUpperCase();
        if (['TRUE', 'T', 'S', 'SIM', '1'].includes(texto)) {
            return true;
        }

        if (['FALSE', 'F', 'N', 'NAO', '0'].includes(texto)) {
            return false;
        }
    }

    return undefined;
}

/**
 * CNPJ com máscara, reusando o mesmo formatador do resto do sistema (`MascaraUtil.cpfCnpj`) — um
 * segundo formatador aqui sairia de sincronia com o das telas de cadastro.
 *
 * Só formata o que tem os 14 dígitos: valor truncado atravessaria a máscara mesmo assim e o card
 * mostraria um CNPJ pela metade com cara de completo. Nesse caso a linha some, como quando falta.
 */
function formatarCnpjResponse(valor?: string): string | undefined {
    const digitos = (valor ?? '').replace(/\D/g, '');
    return digitos.length === 14 ? MascaraUtil.cpfCnpj(digitos) : undefined;
}

function normalizarAvatarMedico(response: Record<string, unknown>): string | undefined {
    const imagem = obterTextoResponse(response, ['avatar', 'foto', 'imagem', 'imagemBase64', 'fotoBase64', 'imFoto', 'imIdentificacao', 'medicoFoto', 'fotoMedico', 'imagemMedico']);

    return normalizarImagemSrc(imagem);
}

function normalizarImagemSrc(valor?: string): string | undefined {
    const texto = (valor ?? '').trim();
    if (!texto) {
        return undefined;
    }

    if (/^(data:image\/|https?:\/\/|blob:)/i.test(texto)) {
        return texto;
    }

    return `data:image/jpeg;base64,${texto}`;
}
